import { Button } from '@/components/UI/Common/Admin/Button';
import { ImageUpload } from '@/components/UI/Common/Admin/ImageUpload';
import { Input } from '@/components/UI/Common/Admin/Input';
import { MultiSelect } from '@/components/UI/Common/Admin/MultiSelect';
import { Select } from '@/components/UI/Common/Admin/Select';
import { applicationScheme } from '@/config/validations';
import { useGetList } from '@/queries/admin';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';

export type FormData = z.infer<typeof applicationScheme>;

export const Form = ({
  formData,
  onSubmit,
  onDelete,
}: {
  onSubmit: (data: FormData) => void;
  formData?: Partial<FormData>;
  onDelete?: VoidFunction;
}) => {
  const { data: applicationGroups } = useGetList('application-groups');
  const { data: userGroups } = useGetList('user-groups');
  const initialFormState = formData
    ? formData
    : ({
        title: '',
        description: '',
        link: '',
        sort: 1,
        deviceType: 'all',
        applicationGroupId: undefined,
        userGroups: [],
        iconLink: {
          url: '',
          rawFile: null,
        },
      } as Partial<FormData>);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
    control,
  } = useForm<FormData>({
    resolver: zodResolver(applicationScheme),
    defaultValues: initialFormState,
    mode: 'onChange',
  });

  const userGroupsOptions = useMemo(
    () => userGroups?.data.map((group) => ({ label: group.name, value: group.id })),
    [userGroups],
  );

  const applicationGroupsOptions = useMemo(
    () => applicationGroups?.data.map((group) => ({ label: group.title, value: group.id })),
    [applicationGroups],
  );

  const deviceTypeOptions = useMemo(
    () => [
      { label: 'Мобильная', value: 'mobile' },
      { label: 'Все платформы', value: 'all' },
      { label: 'Декстоп', value: 'desktop' },
    ],
    [],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
      <Input {...register('title')} error={errors?.title?.message} label="Название" required />
      <Input {...register('description')} label="Описание" />
      <Input {...register('link')} error={errors?.link?.message} label="Ссылка" required />
      <Input
        {...register('sort', { valueAsNumber: true })}
        error={errors?.sort?.message}
        label="Сортировка"
        required
        type="number"
        min={1}
      />
      <Controller
        name="deviceType"
        control={control}
        render={({ field, fieldState }) => (
          <Select
            error={fieldState.error?.message}
            label="Тип устройства"
            options={deviceTypeOptions}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            required
          />
        )}
      />
      <Controller
        name="applicationGroupId"
        control={control}
        render={({ field, fieldState }) => (
          <Select
            error={fieldState.error?.message}
            label="Группы приложений"
            options={applicationGroupsOptions ?? []}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            placeholder="Выберите группу приложений"
          />
        )}
      />
      <Controller
        name="userGroups"
        control={control}
        render={({ field, fieldState }) => (
          <MultiSelect
            error={fieldState.error?.message}
            label="Группы пользователей"
            options={userGroupsOptions ?? []}
            value={field.value || []}
            onChange={(options) => field.onChange(options.map((opt) => opt))}
            placeholder="Выберите группы пользователей"
          />
        )}
      />
      <Controller
        name="iconLink"
        control={control}
        render={({ field, fieldState }) => {
          return (
            <ImageUpload
              error={fieldState.error?.message}
              value={field.value.url}
              onImageChange={(file) => field.onChange({ url: '', rawFile: file })}
            />
          );
        }}
      />

      <div className="flex justify-end gap-4">
        {onDelete && (
          <Button variant="danger" onClick={onDelete} type="button">
            Удалить
          </Button>
        )}
        <Button variant="secondary" disabled={!isDirty} onClick={() => reset()} type="button">
          Сбросить
        </Button>
        <Button disabled={!isValid || !isDirty} type="submit">
          Сохранить
        </Button>
      </div>
    </form>
  );
};
